// §4.2 action bar panels — Cut · Re-measure · Move · Quarantine/Release ·
// Retire. Each one is a real §5 call, each is gated by the §7 code the server
// enforces, and each renders its own refusal (the module's problem codes are
// specific enough to be actionable: INSUFFICIENT_REMAINING carries the FRESH
// remaining so the operator can re-enter, MOVEMENT_ILLEGAL names the §2.4 rule).
//
// Everything a panel writes is a MEASURE, so every input is exact decimal text
// handed to the API verbatim — no parseFloat anywhere on a write path.

import { useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import type { components } from "@assessly/api-client";
import { Button } from "../../../../../components/button";
import { DataTable } from "../../../../../components/data-table";
import { CheckboxField, Field } from "../../../../../components/field";
import { Select } from "../../../../../components/select";
import { LocationPicker } from "../../components/location-picker";
import {
  useCreateMovement,
  useCutUnit,
  useQuarantineUnit,
  useReleaseUnit,
  useRemeasureUnit,
  useRetireUnit,
  type StockUnit,
} from "../../hooks/inventory-queries";
import { inventoryErrorMessage, problemExtension } from "../../lib/errors";
import { formatMeasure, typedMeasure, formatMeasureWithUom } from "../../lib/measure";

type S = components["schemas"];

const REASON_CODES: readonly S["UnitRetireRequest"]["reason_code"][] = [
  "damage",
  "loss",
  "shrinkage",
  "data_correction",
];

export type UnitAction = "cut" | "remeasure" | "move" | "quarantine" | "release" | "retire";

export function UnitActionPanel({
  action,
  unit,
  onClose,
}: {
  action: UnitAction;
  unit: StockUnit;
  onClose: () => void;
}) {
  switch (action) {
    case "cut":
      return <CutPanel unit={unit} onClose={onClose} />;
    case "remeasure":
      return <RemeasurePanel unit={unit} onClose={onClose} />;
    case "move":
      return <MovePanel unit={unit} onClose={onClose} />;
    case "quarantine":
      return <QuarantinePanel unit={unit} onClose={onClose} />;
    case "release":
      return <ReleasePanel unit={unit} onClose={onClose} />;
    case "retire":
      return <RetirePanel unit={unit} onClose={onClose} />;
  }
}

function PanelShell({
  title,
  children,
  onClose,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <section className="inv-panel" data-testid="unit-panel-shell" aria-label={title}>
      <div className="inv-panel-head">
        <h2>{title}</h2>
        <Button className="inv-panel-close" onClick={onClose}>
          {t("inventory.common.close")}
        </Button>
      </div>
      {children}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Cut (§3.2)
// ---------------------------------------------------------------------------

interface PieceDraft {
  readonly id: number;
  length: string;
  kind: "child" | "waste";
  serial: string;
}

function CutPanel({ unit, onClose }: { unit: StockUnit; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const cut = useCutUnit();
  const [pieces, setPieces] = useState<PieceDraft[]>([
    { id: 1, length: "", kind: "child", serial: "" },
  ]);
  const [kerf, setKerf] = useState("");
  const [qcFail, setQcFail] = useState(false);

  const fresh = problemExtension(cut.error, "remaining_measure");

  if (cut.isSuccess) {
    return (
      <PanelShell title={t("inventory.cut.title")} onClose={onClose}>
        <p className="inv-success" data-testid="cut-done" role="status">
          {t("inventory.cut.done", {
            n: cut.data.children.length,
            remaining: formatMeasure(cut.data.parent_remaining_measure, { locale: i18n.language }),
            uom: unit.measure_uom ?? "",
          })}
        </p>
        <ul className="inv-list" data-testid="cut-children">
          {cut.data.children.map((child) => (
            <li
              key={child.id}
              data-testid="cut-child"
              data-serial={child.serial_code}
              data-remaining={child.remaining_measure}
              data-offcut={String(child.is_offcut)}
              data-quarantined={String(child.quarantined)}
            >
              <strong>{child.serial_code}</strong>{" "}
              {formatMeasureWithUom(child.remaining_measure, unit.measure_uom, {
                locale: i18n.language,
              })}
              {child.is_offcut ? ` · ${t("inventory.cut.offcut")}` : ""}
              {child.quarantined ? ` · ${t("inventory.vocab.qc.quarantined")}` : ""}
            </li>
          ))}
        </ul>
        {cut.data.waste_pieces.length > 0 ? (
          <p className="inv-hint">
            {t("inventory.cut.waste", {
              total: cut.data.waste_pieces
                .map((w) => formatMeasure(w.length, { locale: i18n.language }))
                .join(" + "),
              uom: unit.measure_uom ?? "",
            })}
          </p>
        ) : null}
        {cut.data.reservation_not_covered === true ? (
          <p className="form-error" role="alert">
            {t("inventory.cut.reservationNotCovered")}
          </p>
        ) : null}
      </PanelShell>
    );
  }

  return (
    <PanelShell title={t("inventory.cut.title")} onClose={onClose}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          const body: S["CutRequest"] = {
            pieces: pieces
              .filter((piece) => piece.length.trim().length > 0)
              .map((piece) => ({
                length: typedMeasure(piece.length, i18n.language),
                kind: piece.kind,
                ...(piece.serial.trim().length > 0 ? { serial_code: piece.serial.trim() } : {}),
              })),
          };
          if (kerf.trim().length > 0) body.kerf = typedMeasure(kerf, i18n.language);
          if (qcFail) body.reason = "qc_fail";
          cut.mutate({ id: unit.id, body });
        }}
      >
        <p className="inv-hint">
          {t("inventory.cut.sourceRemaining", {
            remaining: formatMeasure(unit.remaining_measure, { locale: i18n.language }),
            uom: unit.measure_uom ?? "",
          })}
        </p>
        <DataTable
          columns={[
            {
              key: "length",
              header: t("inventory.cut.colLength", { uom: unit.measure_uom ?? "" }),
              render: (piece) => (
                <input
                  data-testid="cut-piece-length"
                  className="ds-input"
                  value={piece.length}
                  inputMode="decimal"
                  onChange={(event) => {
                    const next = event.target.value;
                    setPieces((prev) =>
                      prev.map((p) => (p.id === piece.id ? { ...p, length: next } : p)),
                    );
                  }}
                  required
                />
              ),
            },
            {
              key: "kind",
              header: t("inventory.cut.colKind"),
              render: (piece) => (
                <Select
                  data-testid="cut-piece-kind"
                  value={piece.kind}
                  onChange={(event) => {
                    const next = event.target.value as "child" | "waste";
                    setPieces((prev) =>
                      prev.map((p) => (p.id === piece.id ? { ...p, kind: next } : p)),
                    );
                  }}
                >
                  <option value="child">{t("inventory.cut.kindChild")}</option>
                  <option value="waste">{t("inventory.cut.kindWaste")}</option>
                </Select>
              ),
            },
            {
              key: "serial",
              header: t("inventory.cut.colSerial"),
              render: (piece) => (
                <input
                  className="ds-input"
                  value={piece.serial}
                  placeholder={t("inventory.cut.serialAuto")}
                  onChange={(event) => {
                    const next = event.target.value;
                    setPieces((prev) =>
                      prev.map((p) => (p.id === piece.id ? { ...p, serial: next } : p)),
                    );
                  }}
                />
              ),
            },
            {
              // The header used to be a bare `<th/>`. A header is the phone
              // card's field label, so an unnamed column printed nothing above
              // the Remove control; the cell is self-labelling either way, but
              // the desktop header now says what the column is for.
              key: "actions",
              header: t("inventory.common.actions"),
              actions: true,
              render: (piece) =>
                pieces.length > 1 ? (
                  <Button
                    onClick={() => {
                      setPieces((prev) => prev.filter((p) => p.id !== piece.id));
                    }}
                  >
                    {t("inventory.common.remove")}
                  </Button>
                ) : null,
            },
          ]}
          rows={pieces}
          rowKey={(piece) => String(piece.id)}
          empty={null}
        />
        <Button
          data-testid="cut-add-piece"
          onClick={() => {
            setPieces((prev) => [
              ...prev,
              { id: (prev[prev.length - 1]?.id ?? 0) + 1, length: "", kind: "child", serial: "" },
            ]);
          }}
        >
          {t("inventory.cut.addPiece")}
        </Button>

        <Field label={t("inventory.cut.kerf", { uom: unit.measure_uom ?? "" })}>
          <input
            className="ds-input"
            value={kerf}
            inputMode="decimal"
            onChange={(event) => {
              setKerf(event.target.value);
            }}
          />
        </Field>
        <p className="inv-hint">{t("inventory.cut.kerfHint")}</p>

        <CheckboxField label={t("inventory.cut.qcFail")} checked={qcFail} onChange={setQcFail} />

        {cut.isError ? (
          <p className="form-error" data-testid="cut-error" role="alert">
            {inventoryErrorMessage(t, cut.error, "inventory.cut.failed")}
            {fresh !== undefined
              ? ` ${t("inventory.cut.freshRemaining", {
                  remaining: formatMeasure(fresh, { locale: i18n.language }),
                  uom: unit.measure_uom ?? "",
                })}`
              : ""}
          </p>
        ) : null}

        <div className="form-actions">
          <Button
            type="submit"
            variant="primary"
            data-testid="cut-submit"
            busy={cut.isPending}
            busyLabel={t("inventory.cut.saving")}
          >
            {t("inventory.cut.submit")}
          </Button>
        </div>
      </form>
    </PanelShell>
  );
}

// ---------------------------------------------------------------------------
// Re-measure (§3.2 step 6 / §9.4)
// ---------------------------------------------------------------------------

function RemeasurePanel({ unit, onClose }: { unit: StockUnit; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const remeasure = useRemeasureUnit();
  const [value, setValue] = useState(unit.remaining_measure ?? "");

  return (
    <PanelShell title={t("inventory.remeasure.title")} onClose={onClose}>
      {remeasure.isSuccess ? (
        <p className="inv-success" role="status">
          {remeasure.data.adjusted
            ? t("inventory.remeasure.adjusted", {
                delta: formatMeasure(remeasure.data.delta, { locale: i18n.language }),
                uom: unit.measure_uom ?? "",
              })
            : t("inventory.remeasure.withinTolerance")}
        </p>
      ) : (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            remeasure.mutate({
              id: unit.id,
              entered_measure: typedMeasure(value, i18n.language),
            });
          }}
        >
          <p className="inv-hint" data-testid="remeasure-container-hint">
            {t("inventory.remeasure.containerHint")}
          </p>
          <p className="inv-hint">
            {t("inventory.remeasure.current", {
              remaining: formatMeasure(unit.remaining_measure, { locale: i18n.language }),
              uom: unit.measure_uom ?? "",
            })}
          </p>
          <Field label={t("inventory.remeasure.entered", { uom: unit.measure_uom ?? "" })}>
            <input
              className="ds-input"
              value={value}
              inputMode="decimal"
              onChange={(event) => {
                setValue(event.target.value);
              }}
              required
            />
          </Field>
          {remeasure.isError ? (
            <p className="form-error" role="alert">
              {inventoryErrorMessage(t, remeasure.error, "inventory.remeasure.failed")}
            </p>
          ) : null}
          <div className="form-actions">
            <Button
              type="submit"
              variant="primary"
              busy={remeasure.isPending}
              busyLabel={t("inventory.remeasure.saving")}
            >
              {t("inventory.remeasure.submit")}
            </Button>
          </div>
        </form>
      )}
    </PanelShell>
  );
}

// ---------------------------------------------------------------------------
// Move (§5 row 25 `repack` — the only movement type that may name a unit)
// ---------------------------------------------------------------------------

/** §2.4: a `repack` may land in a physical storage location, never in one of the
 * 8 system/virtual ones. Applied to the picker's RESULTS (bounded — see
 * location-picker.tsx's header), so the search itself stays server-side. */
const DESTINATION_KINDS = ["bin", "zone"] as const;

function MovePanel({ unit, onClose }: { unit: StockUnit; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const move = useCreateMovement();
  const [to, setTo] = useState("");

  const held = move.isSuccess && move.data.status === 202;

  return (
    <PanelShell title={t("inventory.move.title")} onClose={onClose}>
      {move.isSuccess ? (
        <p className="inv-success" role="status">
          {held ? t("inventory.move.heldForApproval") : t("inventory.move.done")}
          {/* A 202 means NOTHING was posted and an `adjustment_requests` row is
              now waiting on someone. Saying so and stopping there is how the
              request used to vanish from the product — §4.11 is where it
              lives, and the requester can cancel it there. */}
          {held ? (
            <>
              {" "}
              <Link to="/inventory/approvals">{t("inventory.move.openApprovals")}</Link>
            </>
          ) : null}
        </p>
      ) : (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (unit.current_location_id === null || unit.remaining_measure === null) return;
            move.mutate({
              movement_type: "repack",
              stock_unit_id: unit.id,
              from_location_id: unit.current_location_id,
              to_location_id: to,
              quantity: unit.remaining_measure,
              uom: unit.measure_uom ?? "pcs",
              ...(unit.product_id !== null ? { product_id: unit.product_id } : {}),
              ...(unit.lot_id !== null ? { lot_id: unit.lot_id } : {}),
            });
          }}
        >
          {unit.current_location_id === null ? (
            <p className="form-error" role="alert">
              {t("inventory.move.noOrigin")}
            </p>
          ) : null}
          <p className="inv-hint">
            {t("inventory.move.moving", {
              quantity: formatMeasure(unit.remaining_measure, { locale: i18n.language }),
              uom: unit.measure_uom ?? "",
            })}
          </p>
          {/* Type-to-search against the SERVER (§5 row 1 `q`). The old select
              offered one 50-row page of this facility's tree, so a storekeeper
              could not move a unit to any bin past row 50 — on a WRITE path,
              with nothing on screen saying the list was cut. */}
          <LocationPicker
            testId="move-destination"
            label={t("inventory.move.destination")}
            value={to}
            onChange={setTo}
            facilityId={unit.facility_id}
            isActive
            kinds={DESTINATION_KINDS}
          />
          {move.isError ? (
            <p className="form-error" role="alert">
              {inventoryErrorMessage(t, move.error, "inventory.move.failed")}
            </p>
          ) : null}
          <div className="form-actions">
            <Button
              type="submit"
              variant="primary"
              busy={move.isPending}
              busyLabel={t("inventory.move.saving")}
              disabled={to.length === 0 || unit.current_location_id === null}
            >
              {t("inventory.move.submit")}
            </Button>
          </div>
        </form>
      )}
    </PanelShell>
  );
}

// ---------------------------------------------------------------------------
// Quarantine / Release (§2.4's two modes: status-only vs relocate)
// ---------------------------------------------------------------------------

function QuarantinePanel({ unit, onClose }: { unit: StockUnit; onClose: () => void }) {
  const { t } = useTranslation();
  const quarantine = useQuarantineUnit();
  const [reason, setReason] = useState("");
  const [relocate, setRelocate] = useState(false);

  return (
    <PanelShell title={t("inventory.quarantine.title")} onClose={onClose}>
      {quarantine.isSuccess ? (
        <p className="inv-success" role="status">
          {t("inventory.quarantine.done")}
        </p>
      ) : (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            quarantine.mutate({ id: unit.id, reason: reason.trim(), relocate });
          }}
        >
          <Field label={t("inventory.quarantine.reason")}>
            <textarea
              value={reason}
              onChange={(event) => {
                setReason(event.target.value);
              }}
              required
            />
          </Field>
          <CheckboxField
            label={t("inventory.quarantine.relocate")}
            checked={relocate}
            onChange={setRelocate}
          />
          <p className="inv-hint">{t("inventory.quarantine.relocateHint")}</p>
          {quarantine.isError ? (
            <p className="form-error" role="alert">
              {inventoryErrorMessage(t, quarantine.error, "inventory.quarantine.failed")}
            </p>
          ) : null}
          <div className="form-actions">
            <Button
              type="submit"
              variant="primary"
              busy={quarantine.isPending}
              disabled={reason.trim().length === 0}
            >
              {t("inventory.quarantine.submit")}
            </Button>
          </div>
        </form>
      )}
    </PanelShell>
  );
}

function ReleasePanel({ unit, onClose }: { unit: StockUnit; onClose: () => void }) {
  const { t } = useTranslation();
  const release = useReleaseUnit();
  const [note, setNote] = useState("");
  const [to, setTo] = useState("");

  return (
    <PanelShell title={t("inventory.releaseUnit.title")} onClose={onClose}>
      {release.isSuccess ? (
        <p className="inv-success" role="status">
          {t("inventory.releaseUnit.done")}
        </p>
      ) : (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            release.mutate({
              id: unit.id,
              ...(note.trim().length > 0 ? { note: note.trim() } : {}),
              ...(to.length > 0 ? { to_location_id: to } : {}),
            });
          }}
        >
          <Field label={t("inventory.releaseUnit.note")}>
            <textarea
              value={note}
              onChange={(event) => {
                setNote(event.target.value);
              }}
            />
          </Field>
          {/* Optional: an empty picker means "release where it stands". Same
              truncation defect as the move destination, same fix. */}
          <LocationPicker
            testId="release-return-to"
            label={t("inventory.releaseUnit.returnTo")}
            value={to}
            onChange={setTo}
            facilityId={unit.facility_id}
            isActive
            kinds={DESTINATION_KINDS}
            clearLabel={t("inventory.releaseUnit.inPlace")}
          />
          {release.isError ? (
            <p className="form-error" role="alert">
              {inventoryErrorMessage(t, release.error, "inventory.releaseUnit.failed")}
            </p>
          ) : null}
          <div className="form-actions">
            <Button type="submit" variant="primary" busy={release.isPending}>
              {t("inventory.releaseUnit.submit")}
            </Button>
          </div>
        </form>
      )}
    </PanelShell>
  );
}

// ---------------------------------------------------------------------------
// Retire (§5 row 19 — `inventory.adjust`, drained containers only)
// ---------------------------------------------------------------------------

function RetirePanel({ unit, onClose }: { unit: StockUnit; onClose: () => void }) {
  const { t } = useTranslation();
  const retire = useRetireUnit();
  const [reasonCode, setReasonCode] = useState<S["UnitRetireRequest"]["reason_code"]>("damage");
  const [confirmed, setConfirmed] = useState(false);

  return (
    <PanelShell title={t("inventory.retire.title")} onClose={onClose}>
      {retire.isSuccess ? (
        <p className="inv-success" role="status">
          {t("inventory.retire.done")}
        </p>
      ) : (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            retire.mutate({ id: unit.id, reason_code: reasonCode });
          }}
        >
          <p className="inv-hint">{t("inventory.retire.hint")}</p>
          <Field label={t("inventory.retire.reasonCode")}>
            <Select
              value={reasonCode}
              onChange={(event) => {
                setReasonCode(event.target.value as S["UnitRetireRequest"]["reason_code"]);
              }}
            >
              {REASON_CODES.map((code) => (
                <option key={code} value={code}>
                  {t(`inventory.vocab.reason.${code}`)}
                </option>
              ))}
            </Select>
          </Field>
          <CheckboxField
            label={t("inventory.retire.confirm", { serial: unit.serial_code })}
            checked={confirmed}
            onChange={setConfirmed}
          />
          {retire.isError ? (
            <p className="form-error" role="alert">
              {inventoryErrorMessage(t, retire.error, "inventory.retire.failed")}
            </p>
          ) : null}
          <div className="form-actions">
            <Button type="submit" variant="primary" busy={retire.isPending} disabled={!confirmed}>
              {t("inventory.retire.submit")}
            </Button>
          </div>
        </form>
      )}
    </PanelShell>
  );
}
